import { BellRing, LayoutDashboard, Users, Waves } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import { PUBLIC_CONTAINER } from './publicShell'

const HEADING_ID = 'how-it-works-heading'

interface Feature {
  icon: LucideIcon
  title: string
  description: string
}

/**
 * Título em duas linhas: a primeira escura, a segunda no azul da marca.
 *
 * Separado em duas chaves, e não numa string com `\n`, porque a quebra é do
 * design e não do texto — no meio da frase ela não existiria.
 */
const HEADING = {
  dark: 'Menos planilha.',
  accent: 'Mais tempo na água.',
} as const

const LEAD =
  'Tudo que a escola precisa para rodar — agenda, alunos, cobrança e avisos — num sistema só.'

/**
 * Os quatro destaques do sistema.
 *
 * Sem numeração, e em `<ul>`: ao contrário do "como funciona" da Houser, que é um
 * passo a passo, aqui a ordem não significa nada — numerar sugeriria uma sequência
 * que não existe.
 */
const FEATURES: Feature[] = [
  {
    icon: LayoutDashboard,
    title: 'Gestão fácil',
    description:
      'Agenda, alunos, pacotes e financeiro num painel só — sem planilha paralela.',
  },
  {
    icon: Users,
    title: 'CRM',
    description:
      'Veja quem sumiu, quem está para renovar e quem nunca voltou depois da aula experimental.',
  },
  {
    icon: BellRing,
    title: 'Notificações automáticas',
    description:
      'Lembrete de aula, aviso de crédito acabando e cobrança saem sozinhos, na hora certa.',
  },
  {
    icon: Waves,
    title: 'Foque no que importa',
    description:
      'A burocracia roda no automático. Você fica com a água, com os alunos e com o mar.',
  },
]

/**
 * A ilustração é uma colagem de três fotos, montada aqui e não num arquivo só:
 * assim dá para trocar uma foto sem reexportar a arte inteira, e cada uma carrega
 * no tamanho que o slot pede.
 *
 * A da esquerda é retrato de propósito — ocupa as duas linhas e sustenta a
 * composição; as outras duas são paisagem e empilham ao lado.
 */
const COLLAGE = {
  tall: {
    src: '/lp/how-agenda.webp',
    alt: 'Mão segurando um celular com a agenda da BeWave aberta, e o painel da escola no notebook atrás',
  },
  top: {
    src: '/lp/how-escola.webp',
    alt: 'Alunos e instrutor conversando no balcão de uma escola de surf',
  },
  bottom: {
    src: '/lp/how-praia.webp',
    alt: 'Dois surfistas caminhando pela praia com as pranchas debaixo do braço',
  },
} as const

/**
 * "Como funciona": a seção de abertura da landing, acima do carrossel de banners.
 *
 * Estrutura portada da Houser (`apps/web-br/src/components/HowItWorks`): coluna de
 * texto com título de duas cores, chamada e os passos numerados; ilustração ao
 * lado. O laranja de lá vira o azul da marca daqui, pelos tokens de
 * `application.css` — os mesmos do gradiente dos banners.
 */
export default function HowItWorks() {
  return (
    <section
      className="w-full pt-12 pb-16 sm:pb-20 lg:pt-20"
      aria-labelledby={HEADING_ID}
    >
      <div className={PUBLIC_CONTAINER}>
        <div className="flex flex-col items-center gap-12 lg:grid lg:grid-cols-[1fr_1.1fr] lg:gap-16">
          {/* Some no tablet: entre md e lg não sobra largura para as duas colunas,
              e a colagem empurraria os destaques para longe demais do título. */}
          <div className="order-2 grid grid-cols-2 grid-rows-2 gap-3 md:hidden lg:block lg:grid">
            <CollagePhoto
              photo={COLLAGE.tall}
              className="row-span-2 h-full min-h-[20rem]"
            />
            <CollagePhoto photo={COLLAGE.top} />
            <CollagePhoto photo={COLLAGE.bottom} />
          </div>

          <div className="order-1 flex w-full flex-col gap-6 md:mx-auto md:max-w-[640px] lg:mx-0 lg:max-w-none lg:gap-8">
            <div>
              <h2
                id={HEADING_ID}
                className="mb-3 text-[1.75rem] font-extrabold leading-[1.3] text-gray-900 sm:text-3xl lg:text-[2.5rem]"
              >
                {HEADING.dark}
                <br />
                <span className="text-lp-blue">{HEADING.accent}</span>
              </h2>
              <p className="text-base font-medium leading-[1.4] text-gray-600 sm:text-lg">
                {LEAD}
              </p>
            </div>

            <ul className="flex flex-col gap-2">
              {FEATURES.map((feature) => (
                <li key={feature.title}>
                  <FeatureRow feature={feature} />
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  )
}

function FeatureRow({ feature }: { feature: Feature }) {
  const { icon: Icon, title, description } = feature

  return (
    <div className="flex items-start gap-6 rounded-2xl p-4">
      <div
        className={cn(
          'flex shrink-0 items-center justify-center rounded-2xl border p-3',
          'border-lp-blue text-lp-blue',
        )}
      >
        <Icon className="size-8" strokeWidth={2} aria-hidden="true" />
      </div>

      <div className="flex flex-col gap-1.5">
        <p className="text-[0.9375rem] font-bold leading-[1.3] text-gray-900 sm:text-base">
          {title}
        </p>
        <p className="text-[0.8125rem] leading-[1.4] text-gray-600 sm:text-sm">
          {description}
        </p>
      </div>
    </div>
  )
}

function CollagePhoto({
  photo,
  className,
}: {
  photo: { src: string; alt: string }
  className?: string
}) {
  return (
    <img
      src={photo.src}
      alt={photo.alt}
      loading="lazy"
      className={cn(
        'h-full w-full rounded-3xl object-cover',
        // Sem altura mínima as duas da direita colapsariam antes da foto carregar
        // e a colagem daria um pulo de layout no primeiro paint.
        'min-h-[9.5rem]',
        className,
      )}
    />
  )
}
